import { useState } from 'react';
import { toast } from '@qodo/design-system';
import { BitbucketWebhookResult } from 'network/httpClients/RepositoryClient/api.schema';
import { useBitbucketInstallWebhooksMutation } from 'network/mutations/useBitbucketInstallWebhooksMutation';
import { InstallationStatusMessage, Step } from 'shared/InstallationDialog';
import { spawn } from 'utils/translations';
import { useBitbucketInstallation } from '../bitbucketInstallationContext';

const spawnCheck = spawn('dialogs.bitbucket-installation.check-connection');
const spawnErrors = spawn('dialogs.bitbucket-installation.errors');

export const CheckConnection = () => {
  const { bitbucketWorkspaceSlug, webhookResults, setWebhookResults } =
    useBitbucketInstallation();

  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());

  const { mutate: installWebhooks } = useBitbucketInstallWebhooksMutation();

  const onRetry = (result: BitbucketWebhookResult) => {
    if (!bitbucketWorkspaceSlug || !result.providerRepoId) {
      toast.error(spawnErrors('webhook-install-failed'));

      return;
    }

    setRetryingIds((prev) => new Set(prev).add(result.repositoryId));

    installWebhooks(
      {
        bitbucketWorkspaceSlug,
        providerRepoIds: [result.providerRepoId],
      },
      {
        onSuccess: (data) => {
          const retryResult = data.results[0];

          if (retryResult) {
            setWebhookResults((prev) =>
              prev.map((r) =>
                r.repositoryId === result.repositoryId ? retryResult : r,
              ),
            );
          }

          setRetryingIds((prev) => {
            const next = new Set(prev);

            next.delete(result.repositoryId);

            return next;
          });
        },
        onError: () => {
          toast.error(spawnErrors('webhook-install-failed'));
          setRetryingIds((prev) => {
            const next = new Set(prev);

            next.delete(result.repositoryId);

            return next;
          });
        },
      },
    );
  };

  return (
    <Step>
      <div className="flex flex-col gap-3">
        {webhookResults.map((result) => {
          const isSuccess = result.status === 'installed';
          const isRetrying = retryingIds.has(result.repositoryId);

          return (
            <InstallationStatusMessage
              key={result.repositoryId}
              status={isSuccess ? 'success' : 'error'}
              message={
                isSuccess
                  ? spawnCheck('success-message', {
                      name: result.repositoryName,
                    })
                  : spawnCheck('error-message', { name: result.repositoryName })
              }
              detail={!isSuccess ? result.error : undefined}
              retryLabel={!isSuccess ? spawnCheck('retry-label') : undefined}
              onRetry={!isSuccess ? () => onRetry(result) : undefined}
              isRetrying={isRetrying}
            />
          );
        })}
      </div>
    </Step>
  );
};
